import {
    calculateItdUtilizationMetrics,
    type ItdUtilizationInput,
} from '../components/pages-equipment/equipmentFieldUtils';
import { umSinceBeginning } from '../components/pages-equipment/equipmentFieldUtils';
import { calculateITDCostPerUM } from './costTrackingUtils';

/**
 * Custom-report keys that are computed per unit from Marks ITD formulas.
 * `itd-cost-per-um` is separate from the cost-financial `$ / UM` key (`cost-per-um`).
 */
export const UTILIZATION_REPORT_METRIC_KEYS = [
    'utilization-pct',
    'projected-vs-actual-um',
    'itd-cost-per-um',
] as const;

export type UtilizationReportMetricKey = (typeof UTILIZATION_REPORT_METRIC_KEYS)[number];

export function isUtilizationReportMetric(key: string): key is UtilizationReportMetricKey {
    return (UTILIZATION_REPORT_METRIC_KEYS as readonly string[]).includes(key);
}

export type UtilizationUnitSource = {
    id: string;
    name?: string | null;
    categoryId?: string | null;
    categoryName?: string | null;
    locationId?: string | null;
    locationName?: string | null;
    putInServiceDate?: string | null;
    aquisitionDate?: string | null;
    inputDate?: string | null;
    lastInspectionDate?: string | null;
    lastDailyServiceDate?: string | null;
    lastInspectionUMCounter?: number | null;
    lastDailyServiceUMCounter?: number | null;
    machineUMCounterAtAquisition?: number | null;
    currentUMCounterValue?: number | null;
    replacedUMCounterValue?: number | null;
    annualProjectedUsage?: number | null;
    utilizationAdjustmentDays?: number | null;
};

export type UtilizationExpenseSource = {
    unitId: string;
    totalExpense?: number | null;
};

/**
 * One row per unit for the utilization custom-report metrics.
 * `createdAt` is omitted so the report date window does not drop a snapshot metric.
 * Utilization % and Projected vs Actual UM % are the same Marks ITD %:
 * (annual pace / annual projected usage) × 100.
 */
export function buildUtilizationReportRecords(
    units: UtilizationUnitSource[],
    expenses: UtilizationExpenseSource[],
): Record<string, unknown>[] {
    const costByUnit = new Map<string, number>();
    for (const expense of expenses) {
        if (!expense.unitId) continue;
        const amount = typeof expense.totalExpense === 'number' && Number.isFinite(expense.totalExpense)
            ? expense.totalExpense
            : 0;
        costByUnit.set(expense.unitId, (costByUnit.get(expense.unitId) ?? 0) + amount);
    }

    return units.map((unit) => {
        const percent = calculateItdUtilizationMetrics(toItdInput(unit))?.percent ?? null;
        const totalCost = costByUnit.get(unit.id) ?? 0;
        const currentUm = umSinceBeginning(unit);

        return {
            id: unit.id,
            asset: unit.name || unit.id,
            location: unit.locationName || unit.locationId || null,
            category: unit.categoryName || unit.categoryId || null,
            utilizationPct: percent,
            projectedVsActualPct: percent,
            itdCostPerUm: calculateITDCostPerUM(totalCost, currentUm),
        };
    });
}

function toItdInput(unit: UtilizationUnitSource): ItdUtilizationInput {
    return {
        putInServiceDate: unit.putInServiceDate,
        aquisitionDate: unit.aquisitionDate,
        inputDate: unit.inputDate,
        lastInspectionDate: unit.lastInspectionDate,
        lastDailyServiceDate: unit.lastDailyServiceDate,
        lastInspectionUMCounter: unit.lastInspectionUMCounter,
        lastDailyServiceUMCounter: unit.lastDailyServiceUMCounter,
        beginningUmAtService: unit.machineUMCounterAtAquisition,
        currentUMCounterValue: unit.currentUMCounterValue,
        replacedUmCounterValue: unit.replacedUMCounterValue,
        annualProjectedUsage: unit.annualProjectedUsage,
        adjustmentDays: unit.utilizationAdjustmentDays,
    };
}
